import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import { Link, withRouter } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { List, Record, Map } from "immutable"
import moment from "moment"
import _isNil from "lodash/isNil"
import ReactTooltip from "react-tooltip"

// ui components
import Paper from "components/UI/elements/Paper"
import PaperHeader from "components/UI/elements/PaperHeader"
import Button from "components/UI/elements/Button"
import StatusElement from "components/UI/elements/StatusElement"
import WorkspaceIcon from "components/UI/elements/WorkspaceIcon"

// helpers
import { getRoutePath } from "routes"
import { formatBytes } from "helpers/dataUnits.helper"
import { goBack } from "helpers/backButton.helper"
import { getComponentIconSrc } from "helpers/component.helper"

// constants
import { MOMENT } from "sharedConstants"

import "./ProjectJobList.css"
import { capitalize } from "helpers/string.helper"

const JOB_LOADING_LIMIT = 20

class ProjectJobList extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      loadingMore: false
    }
  }

  async componentDidMount() {
    const { selectionSettings, listProjectJob } = this.props
    try {
      const limit =
        selectionSettings && selectionSettings.limit > 20
          ? selectionSettings.limit
          : JOB_LOADING_LIMIT
      await listProjectJob(0, limit, 1, "status", "DESC")
      this.intervalId = setInterval(this.refreshProjectJobs, 8000)
    } catch (e) {}
  }

  componentWillUnmount() {
    clearInterval(this.intervalId)
  }

  refreshProjectJobs = async () => {
    const { listProjectJob, selectionSettings } = this.props
    if (!this.state.loadingMore) {
      try {
        await listProjectJob(
          selectionSettings.offset,
          selectionSettings.limit,
          1,
          selectionSettings.order_by,
          selectionSettings.order_dir,
          false
        )
      } catch (e) {}
    }
  }

  renderDataCell = (bytes, files) => {
    if (_isNil(bytes)) {
      return "–"
    }

    return (
      <span className="data-text">
        {formatBytes(bytes)}{" "}
        <span>
          in {files} {files === 1 ? "file" : "files"}
        </span>
      </span>
    )
  }

  renderLoadMoreButton = () => {
    const { selectionSettings, jobs } = this.props
    const { loadingMore } = this.state
    if (List.isList(jobs)) {
      if (
        selectionSettings.limit < 100 &&
        jobs.size < 100 &&
        jobs.size === selectionSettings.limit
      ) {
        return (
          <Button
            size="small"
            color="white"
            className={`load-more-button ${loadingMore ? "loading" : ""}`}
            onClick={this.loadMoreJobs}
          >
            Load more
          </Button>
        )
      }
    }
  }

  loadMoreJobs = async () => {
    const { selectionSettings, listProjectJob } = this.props
    this.setState({
      loadingMore: true
    })
    try {
      await listProjectJob(0, selectionSettings.limit + JOB_LOADING_LIMIT, 1, "status", "DESC")
    } catch (e) {
    } finally {
      this.setState({ loadingMore: false })
    }
  }

  render() {
    const { jobs, history, entityName } = this.props

    return (
      <section className="job-list-page project-jobs">
        {List.isList(jobs) && (
          <React.Fragment>
            <PaperHeader size="small" className="job-list-header">
              <div className="navigation-block">
                <Button
                  className="back-link"
                  onClick={goBack(
                    history,
                    getRoutePath(entityName === "configuration" ? "workspace.list" : "dawg.list")
                  )}
                  size="small"
                  color="none"
                >
                  <FontAwesomeIcon icon={["fas", "chevron-left"]} /> Back
                </Button>
                <h3>All {capitalize(entityName)} Activity</h3>
              </div>
            </PaperHeader>
            <Paper hasHeader className="job-list-content">
              {jobs.size > 0 && (
                <React.Fragment>
                  <div className="table table-content-clickable-row">
                    <div className="thead">
                      <div className="table-row">
                        <div className="table-head hover-overlap-helper">&nbsp;</div>
                        <div className="table-head name">Name</div>
                        <div className="table-head status">Status</div>
                        <div className="table-head">Modified</div>
                        <div className="table-head">Data in</div>
                        <div className="table-head">Data out</div>
                        <div className="table-head align-right">User</div>
                        <div className="table-head hover-overlap-helper">&nbsp;</div>
                      </div>
                    </div>
                    <div className="tbody">
                      {jobs.map(job => {
                        const entity =
                          entityName === "configuration" ? job.configuration : job.workspace
                        const pathname =
                          entityName === "configuration"
                            ? getRoutePath("workspace.configuration.configurationJob.show", {
                                id: entity.getIn(["workspace", "id"]),
                                cid: entity.get("id"),
                                aid: job.id
                              })
                            : getRoutePath("workspace.workspaceJob.show", {
                                id: entity.get("id"),
                                aid: job.id
                              })
                        let entityNameComponent = null
                        if (entityName === "configuration") {
                          entityNameComponent = (
                            <React.Fragment>
                              <img
                                src={getComponentIconSrc(entity.getIn(["component", "icon"]))}
                                alt="component icon"
                                data-tip
                                data-for={`component-toltip-${entity.get("id")}`}
                              />
                              <div className="conf-ws-name-wrapper">
                                <span className="conf-name">{entity.get("name")}</span>
                                <span className="ws-name">
                                  {entity.getIn(["workspace", "name"])}
                                </span>
                              </div>
                              <ReactTooltip
                                id={`component-toltip-${entity.get("id")}`}
                                place="bottom"
                              >
                                {entity.getIn(["component", "name"])}
                              </ReactTooltip>
                            </React.Fragment>
                          )
                        } else {
                          entityNameComponent = (
                            <React.Fragment>
                              <WorkspaceIcon />
                              <div className="conf-ws-name-wrapper">
                                <span className="conf-name">{entity.get("name")}</span>
                                {entity.getIn(["dawg", "id"]) > 0 && (
                                  <span className="ws-name">{entity.getIn(["dawg", "name"])}</span>
                                )}
                              </div>
                            </React.Fragment>
                          )
                        }
                        return (
                          <Link
                            to={{
                              pathname: pathname,
                              state: {
                                previous: this.props.location.pathname
                              }
                            }}
                            className="table-row"
                            key={job.id}
                          >
                            <div className="table-cell hover-overlap-helper">&nbsp;</div>
                            <div className="table-cell">
                              <div className="entity-name">{entityNameComponent}</div>
                            </div>
                            <div className="table-cell">
                              <StatusElement
                                align="left"
                                status={job.status}
                                history={job.stats ? job.stats.get("statuses_history") : Map()}
                                created={job.origin_created ? job.origin_created : job.created}
                                showActualRunningDuration={true}
                                durationOneLine
                              />
                            </div>
                            <div className="table-cell">
                              {moment
                                .utc(job.created)
                                .local()
                                .format(MOMENT.DATE_TIME_FORMAT)}
                            </div>
                            <div className="table-cell">
                              {this.renderDataCell(
                                job.getIn(["stats", "data_volumes", "/in"]),
                                job.getIn(["stats", "files_count", "/in"])
                              )}
                            </div>
                            <div className="table-cell">
                              {this.renderDataCell(
                                job.getIn(["stats", "data_volumes", "/out"]),
                                job.getIn(["stats", "files_count", "/out"])
                              )}
                            </div>
                            <div className="table-cell align-right">{job.user.get("name")}</div>
                            <div className="table-cell hover-overlap-helper">&nbsp;</div>
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                </React.Fragment>
              )}
              {jobs.size === 0 && (
                <p className="no-jobs-message">The project doesn't have an activity history yet.</p>
              )}
            </Paper>
            {this.renderLoadMoreButton()}
          </React.Fragment>
        )}
      </section>
    )
  }
}

ProjectJobList.propTypes = {
  jobs: PropTypes.instanceOf(List),
  selectionSettings: PropTypes.instanceOf(Record),
  listProjectJob: PropTypes.func.isRequired,
  entityName: PropTypes.string.isRequired
}

export default withRouter(ProjectJobList)
